import { useCallback, useMemo, useState } from 'react';

import clsx from 'clsx';
import { createEditor } from 'slate';
import { withHistory } from 'slate-history';
import { Editable, Slate, withReact } from 'slate-react';
import type { RenderElementProps, RenderLeafProps, RenderPlaceholderProps } from 'slate-react';

import { useEditorValue } from '@app/hooks/rich-text/use-editor-value';
import { useRefreshKey } from '@app/hooks/rich-text/use-refresh-key';

import { Element, Leaf, Placeholder } from '../slate-components';
import style from './lite-rich-text-editor.module.scss';
import { Toolbar } from './toolbar';
import type { LiteRichTextEditorProps } from './types';
import {
  createOnDOMBeforeInput,
  createOnKeyDown,
  createOnPaste,
  createOnSlateChange,
} from './utils/editor-utils';

export const LiteRichTextEditor = ({
  nodes,
  autoFocus,
  placeholder,
  className,
  elementClassNames = {},
  onChange: onNodesChange,
}: LiteRichTextEditorProps) => {
  const [editor] = useState(() => withReact(withHistory(createEditor())));
  const { editorValue, onChange } = useEditorValue(nodes, onNodesChange);
  const refreshKey = useRefreshKey(nodes);

  const renderElement = useCallback(
    (elementProps: RenderElementProps) => <Element {...elementProps} />,
    [],
  );
  const renderLeaf = useCallback((leafProps: RenderLeafProps) => <Leaf {...leafProps} />, []);
  const renderPlaceholder = useCallback(
    (placeholderProps: RenderPlaceholderProps) => <Placeholder {...placeholderProps} />,
    [],
  );

  const onSlateChange = useMemo(
    () => createOnSlateChange(editor, onChange),
    [
      editor,
      onChange,
    ],
  );
  const onDOMBeforeInput = useMemo(
    () => createOnDOMBeforeInput(editor),
    [
      editor,
    ],
  );
  const onKeyDown = useMemo(
    () => createOnKeyDown(editor),
    [
      editor,
    ],
  );
  const onPaste = useMemo(
    () => createOnPaste(editor),
    [
      editor,
    ],
  );

  const {
    container: containerClassName,
    editable: editableClassName,
    toolbar: toolbarClassName,
  } = elementClassNames;

  return (
    <div className={clsx(style.LiteRichTextEditor, className)}>
      <Slate
        editor={editor}
        initialValue={editorValue}
        key={refreshKey}
        onChange={onSlateChange}
      >
        <Toolbar className={toolbarClassName} />
        <div className={clsx(style.LiteRichTextEditorEditableContainer, containerClassName)}>
          <Editable
            autoFocus={autoFocus}
            spellCheck={false}
            translate="no"
            placeholder={placeholder}
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            renderPlaceholder={renderPlaceholder}
            className={clsx(style.LiteRichTextEditorEditable, editableClassName)}
            onDOMBeforeInput={onDOMBeforeInput}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
          />
        </div>
      </Slate>
    </div>
  );
};
